'use client';

import { useQuery } from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import { accountKeys } from '@/lib/query-keys';
import { getInvoiceHistoryAction, getInvoiceDetailAction } from './actions';
import type { InvoiceHistoryResponse, InvoiceDetail } from './types';

/**
 * Hook reading the firm's invoice history from GET /account/billing/invoices
 * (LF-4604). Summary rows only; the detail drawer loads line items + receipt
 * links on demand via {@link useInvoiceDetail}. DRAFT invoices are excluded BE-side.
 */
export function useInvoiceHistory() {
  return useQuery<InvoiceHistoryResponse>({
    queryKey: accountKeys.billingInvoices,
    queryFn: () => gatedAction(() => getInvoiceHistoryAction()),
    // Invoices change only when a billing cycle finalizes — a short cache keeps
    // the tab responsive without going stale within a session.
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Hook reading a single invoice's detail (line items + Stripe receipt links)
 * from GET /account/billing/invoices/:id (LF-4604). Enabled only when an
 * invoice id is provided, so the detail drawer fetches lazily on open.
 */
export function useInvoiceDetail(invoiceId: number | null) {
  return useQuery<InvoiceDetail>({
    queryKey:
      invoiceId != null
        ? accountKeys.billingInvoice(invoiceId)
        : accountKeys.billingInvoice(-1),
    queryFn: () =>
      gatedAction(() => getInvoiceDetailAction(invoiceId as number)),
    enabled: invoiceId != null,
    staleTime: 5 * 60 * 1000
  });
}
